import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import { getConversas, type ConversaCanal, canalConfig } from '@/mocks/backoffice/conversasCanais';
import {
  getConversaById,
  atualizarStatusComAuditoriaConversa,
  atribuirResponsavelComAuditoriaConversa,
  gerarTicketAPartirDeConversa,
} from '@/mocks/backoffice/canaisHelpers';
import { criarChamadoManualComAuditoria } from '@/mocks/backoffice/atendimentoAuditoria';
import TicketChat from './[id]/components/TicketChat';
import UserProfilePanel from './[id]/components/UserProfilePanel';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';

export default function BackofficeAtendimentoPage() {
  const navigate = useNavigate();
  const [showNovoTicketModal, setShowNovoTicketModal] = useState(false);
  const [novoTicketFeedback, setNovoTicketFeedback] = useState('');

  const [ticketForm, setTicketForm] = useState({
    titulo: '',
    descricao: '',
    tipo: 'suporte_tecnico' as 'suporte_tecnico' | 'financeiro' | 'duvida' | 'reclamacao',
    prioridade: 'media' as 'baixa' | 'media' | 'alta' | 'urgente',
    usuario_tipo: 'participante' as 'empresa' | 'participante',
    usuario_nome: '',
    usuario_email: '',
    responsavel: 'Ana Souza',
    canal_origem: 'Telefone',
  });

  const { session } = useBackofficeAuth();

  const [conversaSelecionada, setConversaSelecionada] = useState<ConversaCanal | null>(null);
  const [mobileView, setMobileView] = useState<'lista' | 'chat'>('lista');
  const [filtroCanal, setFiltroCanal] = useState<string>('todos');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>('todos');
  const [busca, setBusca] = useState('');

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showGerarTicketModal, setShowGerarTicketModal] = useState(false);
  const [showEncerrarModal, setShowEncerrarModal] = useState(false);
  const [showReabrirModal, setShowReabrirModal] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState('');
  const [showMobileProfile, setShowMobileProfile] = useState(false);

  const usuarioNome = session.nome || 'Usuário';
  const usuarioRole = session.role || 'operador';

  // --- Configs ---
  const statusConfig: Record<string, { bg: string; text: string; label: string; dot: string }> = {
    aberto: { bg: 'bg-red-50', text: 'text-red-600', label: 'Aberto', dot: 'bg-red-500' },
    em_andamento: { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Em andamento', dot: 'bg-blue-500' },
    aguardando: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Aguardando', dot: 'bg-amber-500' },
    resolvido: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Resolvido', dot: 'bg-emerald-500' },
    fechado: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Fechado', dot: 'bg-gray-400' },
  };

  const prioridadeConfig: Record<string, { bg: string; text: string }> = {
    baixa: { bg: 'bg-gray-100', text: 'text-gray-600' },
    media: { bg: 'bg-blue-50', text: 'text-blue-600' },
    alta: { bg: 'bg-orange-50', text: 'text-orange-600' },
    urgente: { bg: 'bg-red-50', text: 'text-red-600' },
  };

  const usuarios = [
    { id: 'adm-001', nome: 'Administrador Geral', role: 'admin' },
    { id: 'sup-001', nome: 'Ana Souza', role: 'suporte' },
    { id: 'fin-001', nome: 'Carlos Mendes', role: 'financeiro' },
    { id: 'op-001', nome: 'Mariana Lima', role: 'operador' },
  ];

  // --- Filtered list ---
  const conversasFiltradas = useMemo(() => {
    let list = getConversas().filter((c) => {
      if (filtroCanal !== 'todos' && c.canal !== filtroCanal) return false;
      if (filtroStatus !== 'todos' && c.status !== filtroStatus) return false;
      if (filtroPrioridade !== 'todos' && c.prioridade !== filtroPrioridade) return false;
      if (busca) {
        const q = busca.toLowerCase();
        return (
          c.titulo.toLowerCase().includes(q) ||
          c.usuario_nome.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          (c.usuario_telefone || '').includes(q)
        );
      }
      return true;
    });
    const pMap: Record<string, number> = { urgente: 4, alta: 3, media: 2, baixa: 1 };
    return list.sort((a, b) => {
      const pa = pMap[a.prioridade] || 0;
      const pb = pMap[b.prioridade] || 0;
      if (pb !== pa) return pb - pa;
      return new Date(b.data_atualizacao).getTime() - new Date(a.data_atualizacao).getTime();
    });
  }, [filtroCanal, filtroStatus, filtroPrioridade, busca, statusFeedback]); // statusFeedback to re-trigger on updates

  const stats = {
    chat: getConversas().filter((c) => c.canal === 'chat' && c.status !== 'resolvido' && c.status !== 'fechado').length,
    whatsapp: getConversas().filter((c) => c.canal === 'whatsapp' && c.status !== 'resolvido' && c.status !== 'fechado').length,
    email: getConversas().filter((c) => c.canal === 'email' && c.status !== 'resolvido' && c.status !== 'fechado').length,
    ticket: getConversas().filter((c) => c.canal === 'ticket' && c.status !== 'resolvido' && c.status !== 'fechado').length,
  };

  // --- Selection ---
  const selecionarConversa = (c: ConversaCanal) => {
    setConversaSelecionada(c);
    setMobileView('chat');
    setShowMobileProfile(false);
  };

  const voltarLista = () => {
    setMobileView('lista');
    setConversaSelecionada(null);
  };

  const recarregarConversa = () => {
    if (conversaSelecionada) {
      const atualizado = getConversaById(conversaSelecionada.id);
      if (atualizado) setConversaSelecionada(atualizado);
    }
  };

  // --- Actions ---
  const handleStatusChange = (novoStatus: string) => {
    if (!conversaSelecionada) return;
    if (atualizarStatusComAuditoriaConversa(conversaSelecionada.id, novoStatus as ConversaCanal['status'], usuarioNome, usuarioRole)) {
      recarregarConversa();
      setShowStatusModal(false);
      setStatusFeedback('Status atualizado!');
      setTimeout(() => setStatusFeedback(''), 2000);
    }
  };

  const handleAssign = (responsavel: string) => {
    if (!conversaSelecionada) return;
    if (atribuirResponsavelComAuditoriaConversa(conversaSelecionada.id, responsavel, usuarioNome, usuarioRole)) {
      recarregarConversa();
      setShowAssignModal(false);
      setStatusFeedback('Responsável atualizado!');
      setTimeout(() => setStatusFeedback(''), 2000);
    }
  };

  const handleGerarTicket = () => {
    if (!conversaSelecionada) return;
    const novoTicket = gerarTicketAPartirDeConversa(conversaSelecionada.id, usuarioNome, usuarioRole);
    if (novoTicket) {
      recarregarConversa();
      setShowGerarTicketModal(false);
      setStatusFeedback(`Ticket ${novoTicket} gerado!`);
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleEncerrar = () => {
    if (!conversaSelecionada) return;
    if (atualizarStatusComAuditoriaConversa(conversaSelecionada.id, 'fechado', usuarioNome, usuarioRole)) {
      recarregarConversa();
      setShowEncerrarModal(false);
      setStatusFeedback('Atendimento encerrado!');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleResolver = () => {
    if (!conversaSelecionada) return;
    if (atualizarStatusComAuditoriaConversa(conversaSelecionada.id, 'resolvido', usuarioNome, usuarioRole)) {
      recarregarConversa();
      setStatusFeedback('Chamado resolvido!');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleAguardar = () => {
    if (!conversaSelecionada) return;
    if (atualizarStatusComAuditoriaConversa(conversaSelecionada.id, 'aguardando', usuarioNome, usuarioRole)) {
      recarregarConversa();
      setStatusFeedback('Marcado como aguardando');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleReabrir = () => {
    if (!conversaSelecionada) return;
    if (atualizarStatusComAuditoriaConversa(conversaSelecionada.id, 'aberto', usuarioNome, usuarioRole)) {
      recarregarConversa();
      setShowReabrirModal(false);
      setStatusFeedback('Atendimento reaberto!');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleCriarTicketManual = () => {
    if (!ticketForm.titulo.trim() || !ticketForm.usuario_nome.trim() || !ticketForm.usuario_email.trim()) return;

    const novoId = criarChamadoManualComAuditoria(
      {
        titulo: ticketForm.titulo.trim(),
        descricao: ticketForm.descricao.trim(),
        tipo: ticketForm.tipo,
        prioridade: ticketForm.prioridade,
        usuario_tipo: ticketForm.usuario_tipo,
        usuario_nome: ticketForm.usuario_nome.trim(),
        usuario_email: ticketForm.usuario_email.trim(),
        responsavel: ticketForm.responsavel,
        canal_origem: ticketForm.canal_origem,
      },
      usuarioNome,
      usuarioRole,
    );

    setNovoTicketFeedback(`Ticket ${novoId} criado!`);
    setTimeout(() => setNovoTicketFeedback(''), 3000);

    // Reset form
    setTicketForm({
      titulo: '',
      descricao: '',
      tipo: 'suporte_tecnico',
      prioridade: 'media',
      usuario_tipo: 'participante',
      usuario_nome: '',
      usuario_email: '',
      responsavel: 'Ana Souza',
      canal_origem: 'Telefone',
    });
    setShowNovoTicketModal(false);

    // Redirecionar para a página do ticket
    navigate(`/backoffice/atendimento/${novoId}`);
  };

  const getUltimaMensagemPreview = (c: ConversaCanal) => {
    if (c.respostas.length === 0) return c.titulo;
    const ultima = c.respostas[c.respostas.length - 1];
    return ultima.interna ? '📎 Nota interna' : ultima.mensagem;
  };

  const getUltimaHora = (c: ConversaCanal) => {
    if (c.respostas.length === 0) return new Date(c.data_criacao);
    return new Date(c.respostas[c.respostas.length - 1].data);
  };

  const formatShortTime = (d: Date) => {
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (days === 1) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const st = conversaSelecionada ? (statusConfig[conversaSelecionada.status] || statusConfig.aberto) : null;
  const pr = conversaSelecionada ? (prioridadeConfig[conversaSelecionada.prioridade] || prioridadeConfig.baixa) : null;
  const cCfg = conversaSelecionada ? (canalConfig[conversaSelecionada.canal]) : null;

  return (
    <BackofficeLayout>
      {/* Page header */}
      <div className="mb-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Atendimento</h1>
            <p className="text-sm text-gray-500 mt-0.5">Canais de comunicação em tempo real</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowNovoTicketModal(true)}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1.5 whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm"></i>
              </div>
              <span>Novo Ticket</span>
            </button>
            <div className="flex items-center space-x-2 flex-wrap">
              {([
                { key: 'chat', value: stats.chat, label: 'Chat', icon: 'ri-chat-1-line', color: 'text-blue-600', bg: 'bg-blue-50' },
                { key: 'whatsapp', value: stats.whatsapp, label: 'WhatsApp', icon: 'ri-whatsapp-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                { key: 'email', value: stats.email, label: 'Email', icon: 'ri-mail-line', color: 'text-amber-600', bg: 'bg-amber-50' },
                { key: 'ticket', value: stats.ticket, label: 'Ticket', icon: 'ri-ticket-line', color: 'text-gray-600', bg: 'bg-gray-50' },
              ] as const).map((s) => (
                <button
                  key={s.key}
                  onClick={() => setFiltroCanal(filtroCanal === s.key ? 'todos' : s.key)}
                  className={`${s.bg} rounded-lg px-2.5 py-1.5 flex items-center space-x-1.5 transition-all cursor-pointer ${
                    filtroCanal === s.key ? 'ring-2 ring-emerald-500' : 'hover:brightness-95'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${s.icon} ${s.color} text-xs`}></i>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-sm font-bold text-gray-800">{s.value}</span>
                    <span className="text-[9px] text-gray-500 hidden sm:inline">{s.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-3 border border-gray-100 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm"></i>
            </div>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por título, usuário, telefone ou número..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filtroCanal}
              onChange={(e) => setFiltroCanal(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todos os canais</option>
              <option value="chat">Chat</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="ticket">Ticket</option>
            </select>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todos os status</option>
              <option value="aberto">Aberto</option>
              <option value="em_andamento">Em andamento</option>
              <option value="aguardando">Aguardando</option>
              <option value="resolvido">Resolvido</option>
              <option value="fechado">Fechado</option>
            </select>
            <select
              value={filtroPrioridade}
              onChange={(e) => setFiltroPrioridade(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todas as prioridades</option>
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
            {(filtroCanal !== 'todos' || filtroStatus !== 'todos' || filtroPrioridade !== 'todos' || busca) && (
              <button
                onClick={() => {
                  setFiltroCanal('todos');
                  setFiltroStatus('todos');
                  setFiltroPrioridade('todos');
                  setBusca('');
                }}
                className="px-3 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Limpar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Three-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_360px] gap-0 border border-gray-100 rounded-xl overflow-hidden bg-white min-h-[600px]">
        {/* LEFT: Channel List */}
        <div className={`border-r border-gray-100 bg-white flex flex-col ${mobileView === 'chat' ? 'hidden lg:flex' : 'flex'}`}>
          <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between shrink-0">
            <span className="text-xs font-bold text-gray-500">
              {conversasFiltradas.length} conversa{conversasFiltradas.length !== 1 ? 's' : ''}
            </span>
            <span className="text-[10px] text-gray-400">Ordenado por prioridade</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversasFiltradas.map((c) => {
              const isSelected = conversaSelecionada?.id === c.id;
              const sCfg = statusConfig[c.status];
              const pCfg = prioridadeConfig[c.prioridade];
              const cCfgItem = canalConfig[c.canal];
              const preview = getUltimaMensagemPreview(c);
              const ultHora = getUltimaHora(c);
              const temTicket = !!c.ticket_id;

              return (
                <button
                  key={c.id}
                  onClick={() => selecionarConversa(c)}
                  className={`w-full text-left px-3 py-3 border-b border-gray-50 transition-all cursor-pointer group ${
                    isSelected ? 'bg-emerald-50/60 border-l-4 border-l-emerald-500' : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                  }`}
                >
                  <div className="flex items-start space-x-2.5">
                    <div className="shrink-0 mt-0.5">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${cCfgItem.bg}`}>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cCfgItem.icon} ${cCfgItem.color} text-sm`}></i>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-800 truncate">{c.usuario_nome}</span>
                        <span className="text-[10px] text-gray-400 shrink-0 ml-1">{formatShortTime(ultHora)}</span>
                      </div>
                      <div className="flex items-center space-x-1 mt-0.5">
                        <span className={`inline-flex items-center space-x-0.5 px-1 py-0.5 rounded text-[9px] font-bold uppercase ${cCfgItem.bg} ${cCfgItem.color}`}>
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className={`${cCfgItem.icon} text-[9px]`}></i>
                          </div>
                          <span>{cCfgItem.label}</span>
                        </span>
                        <span className={`inline-flex px-1 py-0.5 rounded text-[9px] font-bold uppercase ${sCfg.bg} ${sCfg.text}`}>
                          {sCfg.label}
                        </span>
                        {!temTicket && (
                          <span className="inline-flex px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-50 text-orange-600">
                            Sem ticket
                          </span>
                        )}
                        {c.origem_manual && (
                          <span className="inline-flex items-center space-x-0.5 px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-stone-100 text-stone-600">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-phone-line text-[8px]"></i>
                            </div>
                            <span>{c.origem_manual}</span>
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 truncate leading-snug">{preview}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[9px] text-gray-400">{c.id}</span>
                        {temTicket && (
                          <span className="text-[9px] text-emerald-600 font-medium">{c.ticket_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {conversasFiltradas.length === 0 && (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-inbox-line text-xl text-gray-300"></i>
                </div>
                <p className="text-xs text-gray-400">Nenhuma conversa encontrada</p>
              </div>
            )}
          </div>
        </div>

        {/* CENTER: Chat Area */}
        <div className={`flex flex-col ${mobileView === 'lista' ? 'hidden lg:flex' : 'flex'}`}>
          {!conversaSelecionada ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-3xl text-gray-300"></i>
              </div>
              <p className="text-sm font-medium text-gray-400">Selecione uma conversa para iniciar o atendimento</p>
              <p className="text-xs text-gray-300 mt-1">O chat em tempo real aparecerá aqui</p>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Chat toolbar */}
              <div className="px-4 py-2.5 border-b border-gray-100 flex flex-col gap-2 shrink-0 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2.5 min-w-0 flex-1">
                    <button
                      onClick={voltarLista}
                      className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-all cursor-pointer shrink-0"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-line text-gray-500 text-sm"></i>
                      </div>
                    </button>

                    <div className={`w-8 h-8 rounded-full ${cCfg?.bg} flex items-center justify-center shrink-0`}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cCfg?.icon} ${cCfg?.color} text-sm`}></i>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-gray-800 truncate" title={conversaSelecionada.titulo}>{conversaSelecionada.titulo}</h3>
                      <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
                        <span className="text-[11px] text-gray-600">{conversaSelecionada.usuario_nome}</span>
                        <span className="text-[10px] text-gray-300">·</span>
                        <span className="text-[11px] text-gray-400 font-mono">{conversaSelecionada.id}</span>
                        {conversaSelecionada.ticket_id && (
                          <>
                            <span className="text-[10px] text-gray-300">·</span>
                            <span className="text-[11px] text-emerald-600 font-medium font-mono">{conversaSelecionada.ticket_id}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1.5 shrink-0 ml-3">
                    {statusFeedback && (
                      <span className="hidden sm:inline text-xs text-emerald-600 font-bold px-2 py-1 bg-emerald-50 rounded">
                        {statusFeedback}
                      </span>
                    )}

                    {/* Acoes rapidas do atendimento */}
                    {conversaSelecionada && (conversaSelecionada.status === 'em_andamento' || conversaSelecionada.status === 'aberto') && (
                      <button
                        onClick={handleAguardar}
                        className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-time-line text-[10px]"></i>
                        </div>
                        <span>Aguardar</span>
                      </button>
                    )}

                    {conversaSelecionada && (conversaSelecionada.status === 'em_andamento' || conversaSelecionada.status === 'aberto') && (
                      <button
                        onClick={handleResolver}
                        className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-check-double-line text-[10px]"></i>
                        </div>
                        <span>Resolver</span>
                      </button>
                    )}

                    {conversaSelecionada && conversaSelecionada.status !== 'fechado' && conversaSelecionada.status !== 'resolvido' && (
                      <button
                        onClick={() => setShowEncerrarModal(true)}
                        className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-close-circle-line text-[10px]"></i>
                        </div>
                        <span>Encerrar</span>
                      </button>
                    )}

                    {conversaSelecionada && (conversaSelecionada.status === 'fechado' || conversaSelecionada.status === 'resolvido') && (
                      <button
                        onClick={() => setShowReabrirModal(true)}
                        className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-restart-line text-[10px]"></i>
                        </div>
                        <span>Reabrir</span>
                      </button>
                    )}

                    {/* Gerar Ticket button - only when no ticket */}
                    {!conversaSelecionada.ticket_id && (
                      <button
                        onClick={() => setShowGerarTicketModal(true)}
                        className="px-2.5 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-ticket-line text-[10px]"></i>
                        </div>
                        <span>Gerar Ticket</span>
                      </button>
                    )}

                    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-bold ${st?.bg} ${st?.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st?.dot} ${conversaSelecionada.status !== 'fechado' && conversaSelecionada.status !== 'resolvido' ? 'animate-pulse' : ''}`}></span>
                      <span>{st?.label}</span>
                    </span>
                    <button
                      onClick={() => setShowStatusModal(true)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
                      title="Alterar status"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-refresh-line text-gray-400 text-sm"></i>
                      </div>
                    </button>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
                      title="Atribuir responsável"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-add-line text-gray-400 text-sm"></i>
                      </div>
                    </button>
                    <button
                      onClick={() => setShowMobileProfile(!showMobileProfile)}
                      className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
                      title="Ver perfil"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line text-gray-400 text-sm"></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Chat */}
              <div className="flex-1 min-h-0">
                <TicketChat conversa={conversaSelecionada} onUpdate={recarregarConversa} />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Profile Panel */}
        <div className={`border-l border-gray-100 bg-white flex flex-col overflow-y-auto ${
          showMobileProfile ? 'fixed inset-0 z-50 lg:static lg:inset-auto' : 'hidden lg:flex'
        }`}>
          {showMobileProfile && (
            <div className="lg:hidden px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
              <span className="text-sm font-bold text-gray-800">Perfil do Usuário</span>
              <button
                onClick={() => setShowMobileProfile(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line text-gray-500 text-sm"></i>
                </div>
              </button>
            </div>
          )}

          {conversaSelecionada ? (
            <div className="p-3 space-y-3">
              <UserProfilePanel
                usuarioEmail={conversaSelecionada.usuario_email}
                usuarioNome={conversaSelecionada.usuario_nome}
                usuarioTipo={conversaSelecionada.usuario_tipo}
              />

              {/* Channel info card */}
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Info do Canal</h3>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Canal</span>
                    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-bold ${cCfg?.bg} ${cCfg?.color}`}>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${cCfg?.icon} text-[9px]`}></i>
                      </div>
                      <span>{cCfg?.label}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Status</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st?.bg} ${st?.text}`}>
                      {st?.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Prioridade</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pr?.bg} ${pr?.text}`}>
                      {conversaSelecionada.prioridade}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Responsável</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
                        <span className="text-[9px] font-bold text-white">{conversaSelecionada.responsavel?.charAt(0) || '?'}</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700">{conversaSelecionada.responsavel || '-'}</span>
                    </div>
                  </div>
                  {conversaSelecionada.ticket_id ? (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">Ticket</span>
                      <span className="text-xs font-bold text-emerald-600">{conversaSelecionada.ticket_id}</span>
                    </div>
                  ) : (
                    <div className="bg-orange-50 rounded-lg p-2.5">
                      <div className="flex items-center space-x-1.5 mb-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-error-warning-line text-orange-500 text-xs"></i>
                        </div>
                        <span className="text-[10px] font-bold text-orange-600">Sem ticket vinculado</span>
                      </div>
                      <button
                        onClick={() => setShowGerarTicketModal(true)}
                        className="w-full py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center space-x-1"
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-ticket-line text-[10px]"></i>
                        </div>
                        <span>Gerar Ticket</span>
                      </button>
                    </div>
                  )}
                  {conversaSelecionada.origem_manual && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">Origem</span>
                      <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-bold bg-stone-100 text-stone-600">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-phone-line text-[9px]"></i>
                        </div>
                        <span>{conversaSelecionada.origem_manual}</span>
                      </span>
                    </div>
                  )}
                  <div className="border-t border-gray-100 pt-2">
                    <span className="text-[10px] text-gray-400">Iniciada em</span>
                    <p className="text-xs text-gray-700">{new Date(conversaSelecionada.data_criacao).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">Atualizada em</span>
                    <p className="text-xs text-gray-700">{new Date(conversaSelecionada.data_atualizacao).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                <i className="ri-user-search-line text-xl text-gray-300"></i>
              </div>
              <p className="text-xs text-gray-400">Selecione uma conversa para ver o perfil</p>
            </div>
          )}
        </div>
      </div>

      {/* Status Modal */}
      {showStatusModal && conversaSelecionada && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowStatusModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-sm font-bold text-gray-800 mb-4">Alterar Status</h3>
              <div className="space-y-2">
                {(['aberto', 'em_andamento', 'aguardando', 'resolvido', 'fechado'] as const).map((s) => {
                  const cfg = statusConfig[s];
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer ${
                        conversaSelecionada.status === s ? 'ring-2 ring-emerald-500 bg-emerald-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                        <span className="text-xs font-medium text-gray-700">{cfg.label}</span>
                      </div>
                      {conversaSelecionada.status === s && <i className="ri-check-line text-emerald-600 text-sm"></i>}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setShowStatusModal(false)}
                className="mt-4 w-full py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        </>
      )}

      {/* Assign Modal */}
      {showAssignModal && conversaSelecionada && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowAssignModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-sm font-bold text-gray-800 mb-4">Atribuir Responsável</h3>
              <div className="space-y-2">
                {usuarios.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleAssign(u.nome)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer ${
                      conversaSelecionada.responsavel === u.nome ? 'ring-2 ring-emerald-500 bg-emerald-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white">{u.nome.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700">{u.nome}</p>
                      <p className="text-[10px] text-gray-400 capitalize">{u.role}</p>
                    </div>
                    {conversaSelecionada.responsavel === u.nome && (
                      <i className="ri-check-line text-emerald-600 text-sm ml-auto"></i>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="mt-4 w-full py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        </>
      )}

      {/* Gerar Ticket Modal */}
      {showGerarTicketModal && conversaSelecionada && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowGerarTicketModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-ticket-line text-orange-600 text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Gerar Ticket</h3>
                  <p className="text-[10px] text-gray-400">A partir desta conversa</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Conversa</span>
                  <span className="text-xs font-medium text-gray-700">{conversaSelecionada.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Canal</span>
                  <span className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${cCfg?.bg} ${cCfg?.color}`}>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`${cCfg?.icon} text-[9px]`}></i>
                    </div>
                    <span>{cCfg?.label}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Usuário</span>
                  <span className="text-xs font-medium text-gray-700">{conversaSelecionada.usuario_nome}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Título</span>
                  <span className="text-xs text-gray-700 truncate max-w-[140px]">{conversaSelecionada.titulo}</span>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-4">
                Um novo ticket será criado com todas as mensagens desta conversa. A auditoria registrará automaticamente a ação.
              </p>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleGerarTicket}
                  className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center space-x-1 whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-ticket-line text-xs"></i>
                  </div>
                  <span>Gerar Ticket</span>
                </button>
                <button
                  onClick={() => setShowGerarTicketModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Encerrar Modal */}
      {showEncerrarModal && conversaSelecionada && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowEncerrarModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-circle-line text-gray-600 text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Encerrar Atendimento</h3>
                  <p className="text-[10px] text-gray-400">Esta acao e irreversivel</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Conversa</span>
                  <span className="text-xs font-bold text-gray-800">{conversaSelecionada.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Status atual</span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st?.bg} ${st?.text}`}>
                    {st?.label}
                  </span>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-4">
                O atendimento sera encerrado e movido para Fechado. O participante sera notificado.
              </p>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleEncerrar}
                  className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Encerrar Atendimento
                </button>
                <button
                  onClick={() => setShowEncerrarModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reabrir Modal */}
      {showReabrirModal && conversaSelecionada && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowReabrirModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-restart-line text-blue-600 text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Reabrir Atendimento</h3>
                  <p className="text-[10px] text-gray-400">Retornar para Aberto</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-4">
                O atendimento sera reaberto e retornara para o status Aberto. Continue o atendimento normalmente.
              </p>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleReabrir}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Reabrir
                </button>
                <button
                  onClick={() => setShowReabrirModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Novo Ticket Modal */}
      {showNovoTicketModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowNovoTicketModal(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center space-x-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-add-circle-line text-emerald-600 text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Novo Ticket</h3>
                  <p className="text-[10px] text-gray-400">Criar chamado manualmente</p>
                </div>
              </div>

              {novoTicketFeedback && (
                <div className="mb-4 p-2.5 bg-emerald-50 rounded-lg text-center">
                  <span className="text-xs text-emerald-600 font-bold">{novoTicketFeedback}</span>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Título *</label>
                  <input
                    type="text"
                    value={ticketForm.titulo}
                    onChange={(e) => setTicketForm({ ...ticketForm, titulo: e.target.value })}
                    placeholder="Ex: Problema no login"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Descrição</label>
                  <textarea
                    value={ticketForm.descricao}
                    onChange={(e) => setTicketForm({ ...ticketForm, descricao: e.target.value })}
                    placeholder="Descreva o problema ou solicitação..."
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all resize-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-1 text-right">{ticketForm.descricao.length}/500</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Tipo *</label>
                    <select
                      value={ticketForm.tipo}
                      onChange={(e) => setTicketForm({ ...ticketForm, tipo: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
                    >
                      <option value="suporte_tecnico">Suporte Técnico</option>
                      <option value="financeiro">Financeiro</option>
                      <option value="duvida">Dúvida</option>
                      <option value="reclamacao">Reclamação</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Prioridade *</label>
                    <select
                      value={ticketForm.prioridade}
                      onChange={(e) => setTicketForm({ ...ticketForm, prioridade: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
                    >
                      <option value="baixa">Baixa</option>
                      <option value="media">Média</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Tipo de usuário *</label>
                    <select
                      value={ticketForm.usuario_tipo}
                      onChange={(e) => setTicketForm({ ...ticketForm, usuario_tipo: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
                    >
                      <option value="participante">Participante</option>
                      <option value="empresa">Empresa</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Responsável *</label>
                    <select
                      value={ticketForm.responsavel}
                      onChange={(e) => setTicketForm({ ...ticketForm, responsavel: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
                    >
                      {usuarios.map((u) => (
                        <option key={u.id} value={u.nome}>{u.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Canal de origem *</label>
                  <select
                    value={ticketForm.canal_origem}
                    onChange={(e) => setTicketForm({ ...ticketForm, canal_origem: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
                  >
                    <option value="Telefone">Telefone</option>
                    <option value="Presencial">Presencial</option>
                    <option value="Email direto">Email direto</option>
                    <option value="Indicação interna">Indicação interna</option>
                    <option value="Reclame Aqui">Reclame Aqui</option>
                    <option value="Procon">Procon</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Nome do usuário *</label>
                  <input
                    type="text"
                    value={ticketForm.usuario_nome}
                    onChange={(e) => setTicketForm({ ...ticketForm, usuario_nome: e.target.value })}
                    placeholder="Nome completo do solicitante"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Email do usuário *</label>
                  <input
                    type="email"
                    value={ticketForm.usuario_email}
                    onChange={(e) => setTicketForm({ ...ticketForm, usuario_email: e.target.value })}
                    placeholder="email@exemplo.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2 mt-5">
                <button
                  onClick={handleCriarTicketManual}
                  disabled={!ticketForm.titulo.trim() || !ticketForm.usuario_nome.trim() || !ticketForm.usuario_email.trim()}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center space-x-1 whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line text-xs"></i>
                  </div>
                  <span>Criar Ticket</span>
                </button>
                <button
                  onClick={() => {
                    setShowNovoTicketModal(false);
                    setTicketForm({
                      titulo: '',
                      descricao: '',
                      tipo: 'suporte_tecnico',
                      prioridade: 'media',
                      usuario_tipo: 'participante',
                      usuario_nome: '',
                      usuario_email: '',
                      responsavel: 'Ana Souza',
                      canal_origem: 'Telefone',
                    });
                  }}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </BackofficeLayout>
  );
}